import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ListPlus, Trash2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export const TASK_STATUSES = ["À faire", "En cours", "Terminé"];

// Menu contextuel (clic droit) d'une alerte du tableau de bord.
export function AlertContextMenu({ menu, onClose, onTask, onDismiss }) {
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => onClose();
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => { window.removeEventListener("click", close); window.removeEventListener("scroll", close, true); };
  }, [menu, onClose]);
  if (!menu) return null;
  return createPortal(
    <div className="fixed z-[95] bg-white rounded-xl shadow-xl border border-brand-soft py-1 w-56" style={{ left: Math.min(menu.x, window.innerWidth - 240), top: Math.min(menu.y, window.innerHeight - 110) }} onClick={(e) => e.stopPropagation()} data-testid="alert-context-menu">
      <button onClick={() => { onTask(menu.alert); onClose(); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-brand-greenDeep hover:bg-brand-softer" data-testid="ctx-create-task"><ListPlus className="w-4 h-4" /> Créer une tâche</button>
      <button onClick={() => { onDismiss(menu.alert); onClose(); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50" data-testid="ctx-dismiss"><Trash2 className="w-4 h-4" /> Supprimer l'information</button>
    </div>,
    document.body
  );
}

export function TaskFromAlertModal({ alert, onClose, onCreated }) {
  const [form, setForm] = useState({ title: alert.title, assignee: "", due: "", status: "À faire" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.title.trim()) { toast.error("Donnez un nom à la tâche"); return; }
    setBusy(true);
    try {
      await api.post("/dashboard/alerts/to-task", { ...form, key: alert.key, kind: alert.kind || "", lease_id: alert.lease_id || "", property_id: alert.property_id || "", tenant: alert.tenant || "", tenant_id: alert.tenant_id || "", piece_kind: alert.piece_kind || "" });
      toast.success("Tâche créée dans « Tâches à venir »");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const inp = "w-full px-3 py-2.5 rounded-xl border border-brand-soft text-sm outline-none focus:border-brand-lime";
  return createPortal(
    <div className="fixed inset-0 z-[96] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="task-from-alert-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Transformer en tâche</h3>
            <p className="text-xs text-brand-green/50 mt-0.5">{alert.subtitle}</p>
          </div>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep" data-testid="tfa-close"><X className="w-5 h-5" /></button>
        </div>
        <label className="block text-xs font-medium text-brand-green/70">Intitulé<input value={form.title} onChange={(e) => set("title", e.target.value)} className={`${inp} mt-1`} data-testid="tfa-title" /></label>
        <label className="block text-xs font-medium text-brand-green/70">Personne en charge<input value={form.assignee} onChange={(e) => set("assignee", e.target.value)} placeholder="ex. Moi, Julie (agence)…" className={`${inp} mt-1`} data-testid="tfa-assignee" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-brand-green/70">Date de fin attendue<input type="date" value={form.due} onChange={(e) => set("due", e.target.value)} className={`${inp} mt-1`} data-testid="tfa-due" /></label>
          <label className="block text-xs font-medium text-brand-green/70">Statut
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className={`${inp} mt-1 bg-white`} data-testid="tfa-status">
              {TASK_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <button onClick={submit} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm disabled:opacity-60" data-testid="tfa-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListPlus className="w-4 h-4" />} Créer la tâche
        </button>
      </div>
    </div>,
    document.body,
  );
}

export function TaskEditModal({ task, onClose, onSaved }) {
  const [form, setForm] = useState({ title: task.title || "", assignee: task.assignee || "", due: /^\d{4}-\d{2}-\d{2}$/.test(task.due || "") ? task.due : "", status: task.status || (task.done ? "Terminé" : "À faire") });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.title.trim()) { toast.error("Donnez un nom à la tâche"); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/tasks/${task.id}`, { ...form, title: form.title.trim(), due: form.due || task.due || "" });
      toast.success("Tâche mise à jour");
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const inp = "w-full px-3 py-2.5 rounded-xl border border-brand-soft text-sm outline-none focus:border-brand-lime";
  return createPortal(
    <div className="fixed inset-0 z-[96] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="task-edit-modal">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Modifier la tâche</h3>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep" data-testid="te-close"><X className="w-5 h-5" /></button>
        </div>
        <label className="block text-xs font-medium text-brand-green/70">Intitulé<input value={form.title} onChange={(e) => set("title", e.target.value)} className={`${inp} mt-1`} data-testid="te-title" /></label>
        <label className="block text-xs font-medium text-brand-green/70">Personne en charge<input value={form.assignee} onChange={(e) => set("assignee", e.target.value)} placeholder="ex. Moi, Julie (agence)…" className={`${inp} mt-1`} data-testid="te-assignee" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-brand-green/70">Date de fin attendue<input type="date" value={form.due} onChange={(e) => set("due", e.target.value)} className={`${inp} mt-1`} data-testid="te-due" /></label>
          <label className="block text-xs font-medium text-brand-green/70">Statut
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className={`${inp} mt-1 bg-white`} data-testid="te-status">
              {TASK_STATUSES.map((st) => <option key={st}>{st}</option>)}
            </select>
          </label>
        </div>
        {task.due && !/^\d{4}-\d{2}-\d{2}$/.test(task.due) && !form.due && <p className="text-[11px] text-brand-green/50">Échéance actuelle : {task.due}</p>}
        <button onClick={save} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm disabled:opacity-60" data-testid="te-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enregistrer
        </button>
      </div>
    </div>,
    document.body,
  );
}
